import { create } from 'zustand';
import {
  OtpReportPayload,
  OtpStage,
  OtpSendRequest,
  OtpSendResponse,
  OtpVerifyRequest,
  OtpVerifyResponse,
  ListingPreviewResponse,
} from '../system/preview';

interface PreviewDraftState {
  reportPayload: OtpReportPayload | null;
  otpStage: OtpStage;
  selectedDelivery: 'email' | 'sms';
  otpError: string | null;
  otpErrorCode: string | null;
  attemptsRemaining: number | null;
  previewToken: string | null;
  listingPreview: ListingPreviewResponse | null;
  isLoading: boolean;
  previewError: string | null;
}

interface PreviewDraftActions {
  setReportPayload: (payload: OtpReportPayload) => void;
  setSelectedDelivery: (method: 'email' | 'sms') => void;
  setOtpStage: (stage: OtpStage) => void;
  sendOtp: (
    apiCall: (request: OtpSendRequest) => Promise<OtpSendResponse>,
    request: OtpSendRequest,
  ) => Promise<void>;
  verifyOtp: (
    apiCall: (request: OtpVerifyRequest) => Promise<OtpVerifyResponse>,
    request: OtpVerifyRequest,
  ) => Promise<void>;
  fetchPreview: (
    apiCall: (
      listingKey: string,
      token?: string,
    ) => Promise<ListingPreviewResponse>,
    listingKey: string,
    token?: string,
  ) => Promise<void>;
  reset: () => void;
}

const initialState: PreviewDraftState = {
  reportPayload: null,
  otpStage: 'delivery_select',
  selectedDelivery: 'email',
  otpError: null,
  otpErrorCode: null,
  attemptsRemaining: null,
  previewToken: null,
  listingPreview: null,
  isLoading: false,
  previewError: null,
};

export const usePreviewDraftStore = create<
  PreviewDraftState & PreviewDraftActions
>((set) => ({
  ...initialState,

  setReportPayload: (payload) => set({ reportPayload: payload }),

  setSelectedDelivery: (method) => set({ selectedDelivery: method }),

  setOtpStage: (stage) => set({ otpStage: stage }),

  sendOtp: async (apiCall, request) => {
    set({ isLoading: true, otpError: null, otpErrorCode: null });
    try {
      const result = await apiCall(request);
      if (result.success) {
        set({
          otpStage: 'code_entry',
          attemptsRemaining: result.attemptsRemaining ?? null,
          isLoading: false,
        });
      } else {
        set({
          otpError: result.error || 'Failed to send verification code',
          otpErrorCode: result.code || null,
          otpStage: 'error',
          isLoading: false,
        });
      }
    } catch (err: any) {
      set({
        otpError:
          err?.message || err?.error || 'Failed to send verification code',
        otpErrorCode: err?.code || null,
        otpStage: 'error',
        isLoading: false,
      });
    }
  },

  verifyOtp: async (apiCall, request) => {
    set({
      isLoading: true,
      otpError: null,
      otpErrorCode: null,
      otpStage: 'verifying',
    });
    try {
      const result = await apiCall(request);
      if (result.success && result.previewToken) {
        set({
          previewToken: result.previewToken,
          otpStage: 'success',
          isLoading: false,
        });
      } else {
        set({
          otpError: result.error || 'Verification failed',
          otpErrorCode: result.code || null,
          attemptsRemaining: result.attemptsRemaining ?? null,
          otpStage: result.code === 'NO_ATTEMPTS_LEFT' ? 'error' : 'code_entry',
          isLoading: false,
        });
      }
    } catch (err: any) {
      set({
        otpError: err?.message || err?.error || 'Verification failed',
        otpErrorCode: err?.code || null,
        otpStage: 'code_entry',
        isLoading: false,
      });
    }
  },

  fetchPreview: async (apiCall, listingKey, token) => {
    set({ isLoading: true, previewError: null });
    try {
      const result = await apiCall(listingKey, token);
      set({ listingPreview: result, isLoading: false });
    } catch (err: any) {
      set({
        previewError:
          err?.message || err?.error || 'Failed to load listing preview',
        isLoading: false,
      });
    }
  },

  reset: () => set(initialState),
}));
